import { useState } from "react";
import { Button } from "@/components/common/button";
import Modal from "@/components/common/generalModal";
import { useGetGender } from "@/pages/quiz/base/hooks/useGetGender";
import { Checkbox } from "@/pages/quiz/base/questions/contactField/components/checkbox";
import { Disclaimer } from "@/pages/quiz/base/questions/contactField/components/disclaimer";
import Description from "@/pages/quiz/base/questions/disclaimerAcknowledgment/components/Description";
import { getDisclaimersDataByProductConsentGroup } from "@/pages/quiz/base/questions/productConsents/disclaimers";
import { useQuizContext } from "@/pages/quiz/context";
import { QuizQuestionProps } from "@/pages/quiz/context/types";

const ProductConsentsQuestion = ({
  question,
  questionValue,
  setQuestionValue,
  setQuestionAnswer,
}: QuizQuestionProps<boolean>) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { productConsentGroup } = question.attributes;
  const { initialQuestions } = useQuizContext();
  const { isMale: isMaleAnswer, isFemale: isFemaleAnswer } = useGetGender();
  // Short quizzes (existing customers) don't ask the sex: the backend already
  // knows it from the order and leaves out `isFemale` questions for men. So
  // with no sex answer, a quiz without any `isFemale` block means a male
  // patient.
  const hasFemaleOnlyQuestion = !!initialQuestions?.some(
    ({ attributes }) => attributes?.isFemale,
  );
  const isMale = isMaleAnswer || (!isFemaleAnswer && !hasFemaleOnlyQuestion);
  const disclaimers = productConsentGroup
    ? getDisclaimersDataByProductConsentGroup(productConsentGroup, isMale)
    : [];

  return (
    <div className="flex flex-col">
      <button
        type="button"
        className="w-fit cursor-pointer text-left text-sm underline"
        onClick={() => setIsModalOpen(true)}
      >
        Consent & Acknowledgement
      </button>
      <p className="text-secondary mt-2 text-sm">
        By clicking Continue, I confirm that I have answered this survey
        truthfully to help tailor my treatment. I agree to the following:
      </p>
      <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-black">
        {disclaimers.map((disclaimer) => (
          <li key={disclaimer.name}>{disclaimer.label}</li>
        ))}
      </ul>
      <div className="mt-2">
        <Checkbox
          isChecked={!!questionValue}
          onChange={() => setQuestionValue(!questionValue)}
          name="consentAcknowledgement"
          label="I have read and agree to the consents and acknowledgments above"
        />
      </div>
      <Button
        disabled={!questionValue || !disclaimers.length}
        onClick={() => setQuestionAnswer(true)}
      />
      <Modal
        backgroundStyle="blur"
        isScrollable
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <div className="mx-auto flex min-h-[calc(100vh-80px)] max-w-[768px] flex-col justify-center gap-4 rounded-3xl sm:gap-8">
          <div className="z-10 flex justify-end bg-white py-2">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              aria-label="Close"
              className="text-sm text-black"
            >
              Close x
            </button>
          </div>
          <p className="section-title">
            Please review and agree to the following precautions
          </p>
          <div className="space-y-4">
            <Description description="Read the following for more information about this product and its potential side effects." />
            {disclaimers.map((disclaimer) => (
              <Disclaimer key={disclaimer.name} disclaimerData={disclaimer} />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIsModalOpen(false)}
            className="text-sm text-black"
            aria-label="Close"
          >
            Close x
          </button>
        </div>
      </Modal>
    </div>
  );
};

export { ProductConsentsQuestion };
